import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import apiService from '../services/api';
import apiWithFallback from '../utils/apiWithFallback';
import toast from 'react-hot-toast';

const DocumentsContext = createContext();

export const useDocuments = () => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error('useDocuments must be used within a DocumentsProvider');
  }
  return context;
};

export const DocumentsProvider = ({ children }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    totalAmount: 0,
    byStatus: {
      pending: 0,
      completed: 0,
      cancelled: 0,
      draft: 0
    },
    byCategory: {
      sales: 0,
      purchase: 0,
      other: 0
    }
  });

  // Load documents
  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get documents from API with localStorage fallback
      const docs = await apiWithFallback.getData(
        'documents',
        apiService.getDocuments,
        'stockpro_documents'
      );
      
      setDocuments(docs || []);
      fetchStats(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
      
      // Try to get from localStorage as fallback
      try {
        const localDocs = localStorage.getItem('stockpro_documents');
        if (localDocs) {
          const parsedDocs = JSON.parse(localDocs);
          setDocuments(parsedDocs);
          fetchStats(parsedDocs);
        }
      } catch (localErr) {
        console.error('Error loading documents from localStorage:', localErr);
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate document statistics
  const fetchStats = (docs = documents) => {
    if (!docs || docs.length === 0) {
      setStats({
        total: 0,
        totalAmount: 0,
        byStatus: { pending: 0, completed: 0, cancelled: 0, draft: 0 },
        byCategory: { sales: 0, purchase: 0, other: 0 }
      });
      return;
    }

    const newStats = {
      total: docs.length,
      totalAmount: docs.reduce((sum, doc) => sum + (doc.total || 0), 0),
      byStatus: {
        pending: docs.filter(doc => doc.status === 'pending').length,
        completed: docs.filter(doc => doc.status === 'completed').length,
        cancelled: docs.filter(doc => doc.status === 'cancelled').length,
        draft: docs.filter(doc => doc.status === 'draft').length
      },
      byCategory: {
        sales: docs.filter(doc => doc.category === 'sales').length,
        purchase: docs.filter(doc => doc.category === 'purchase').length,
        other: docs.filter(doc => doc.category !== 'sales' && doc.category !== 'purchase').length
      }
    };

    setStats(newStats);
  };

  // Create document
  const createDocument = async (documentData) => {
    try {
      // Generate a local ID if none provided
      const id = documentData.id || `doc-${uuidv4()}`;
      const documentWithId = { ...documentData, id };
      
      // Try to create via API
      const createdDocument = await apiWithFallback.createData(
        'documents',
        apiService.createDocument,
        documentWithId,
        'stockpro_documents'
      );
      
      // Update state
      setDocuments(prevDocs => [...prevDocs, createdDocument]);
      fetchStats([...documents, createdDocument]);
      
      toast.success('Document créé avec succès');
      return createdDocument;
    } catch (err) {
      console.error('Error creating document:', err);
      toast.error('Erreur lors de la création du document');
      throw err;
    }
  };

  // Update document
  const updateDocument = async (id, documentData) => {
    try {
      // Try to update via API
      const updatedDocument = await apiWithFallback.updateData(
        'documents',
        apiService.updateDocument,
        id,
        documentData,
        'stockpro_documents'
      );
      
      // Update state
      setDocuments(prevDocs => 
        prevDocs.map(doc => doc.id === id ? updatedDocument : doc)
      );
      fetchStats();
      
      toast.success('Document mis à jour avec succès');
      return updatedDocument;
    } catch (err) {
      console.error('Error updating document:', err);
      toast.error('Erreur lors de la mise à jour du document');
      throw err;
    }
  };

  // Delete document
  const deleteDocument = async (id) => {
    try {
      // Try to delete via API
      await apiWithFallback.deleteData(
        'documents',
        apiService.deleteDocument,
        id,
        'stockpro_documents'
      );
      
      // Update state
      setDocuments(prevDocs => prevDocs.filter(doc => doc.id !== id));
      fetchStats(documents.filter(doc => doc.id !== id));
      
      toast.success('Document supprimé avec succès');
      return { id, deleted: true };
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Erreur lors de la suppression du document');
      throw err;
    }
  };

  // Get document by ID
  const getDocumentById = (id) => {
    return documents.find(doc => doc.id === id);
  };

  // Get documents by entity
  const getDocumentsByEntity = (entityId, entityType) => {
    return documents.filter(
      doc => doc.entityId === entityId && doc.entityType === entityType
    );
  };

  // Initial load
  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DocumentsContext.Provider
      value={{
        documents,
        loading,
        error,
        stats,
        fetchDocuments,
        createDocument,
        updateDocument,
        deleteDocument,
        getDocumentById,
        getDocumentsByEntity,
        refreshDocuments: fetchDocuments
      }}
    >
      {children}
    </DocumentsContext.Provider>
  );
};